import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import media from '../../../constants/media';
import theme from '../../../constants/theme';
import { formatDate } from '../../../helpers/formatting/formatting';
import IArticleListItem from '../../../interfaces/articles/articleListItem';

const Image = styled.img`
	width: 100%;
	height: 224px;
	border-top-right-radius: 8px;
	border-top-left-radius: 8px;
	object-fit: cover;
	transition: all 0.15s ease-in-out;

	@media only screen and (min-width: 480px) {
		height: 350px;
	}

	${media.tablet`
		height: 270px;
	`}

	${media.desktopSmall`
		border-bottom-right-radius: 8px;
		border-bottom-left-radius: 8px;
	`}
`;

const TextContent = styled.span`
	display: flex;
	flex-direction: column;
	flex: 1;
	width: 100%;
	padding: 24px;
`;

const DateSection = styled.span`
	font-size: 16px;
	color: ${theme.colorsV2.grey6};
`;

const TitleAndDescription = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 5;
	-webkit-box-orient: vertical;

	${media.tablet`
		-webkit-line-clamp: 4;
	`}
`;

const Title = styled.h3`
	margin-top: 14px;
	color: ${theme.colorsV2.mainDark};
	font-size: 20px;
	line-height: 1.5;
`;

const Description = styled.p`
	margin: 8px 0 0;
	color: ${theme.colorsV2.grey6};
	font-size: 18px;
	line-height: 1.4;
`;

const CardContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	font-family: ${theme.fonts.Arimo};
	border-radius: 8px;
	box-shadow: 0 0 50px rgba(0, 0, 0, 0.15);

	${media.desktopSmall`
		box-shadow: none;
	`}
`;

const ClickOverlay = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	border-radius: 8px;
	cursor: pointer;

	${media.desktopSmall`
		transition: box-shadow 0.15s ease-in-out;
		&:hover {
			box-shadow: 0 0 26px rgba(0, 0, 0, 0.15);

			& + ${Image} {
				border-bottom-right-radius: 0;
				border-bottom-left-radius: 0;
			}
		}
	`}
`;

interface Props {
	article: IArticleListItem;
}

const NewsCard: FC<Props> = ({ article }) => {
	const history = useHistory();
	const [mouseMoved, setMouseMoved] = useState(false);

	const handleClick = (to: string) => {
		if (!mouseMoved) {
			history.push(to);
		}
	};

	return (
		<CardContainer>
			<ClickOverlay
				role="link"
				data-href={article.Url}
				tabIndex={0}
				onMouseMove={() => setMouseMoved(true)}
				onMouseDown={() => setMouseMoved(false)}
				onMouseUp={() => handleClick(article.Url)}
			/>
			<Image src={article.Image.Src} alt={article.Image.Name} sizes="(max-width: 720px), (max-height: 350px)" />
			<TextContent>
				<DateSection>{formatDate(article.Date)}</DateSection>
				<TitleAndDescription>
					<Title>{article.Title}</Title>
					<Description>{article.Summary}</Description>
				</TitleAndDescription>
			</TextContent>
		</CardContainer>
	);
};

export default NewsCard;
